import { useCallback } from 'react';

import { MbscEventUpdatedEvent } from '@mobiscroll/react';

import { useAppDispatch, useAppSelector } from 'src/app/store/hooks';
import { usePayers } from 'src/features/add-trip/Pricing/usePayers';

import { getExistingMultiloadTripIdsForAssignment } from '../../common/getExistingMultiloadTripIdsForAssignment';
import { getPotentialMultiloadCandidatesForUnassignedTrip } from '../../common/getPotentialMultiloadCandidatesForUnassignedTrip';
import { dispatcherSlice } from '../../dispatcher.slice';
import { useTripMenuActions } from '../useTripMenuActions';

export function useOnEventUpdated() {
  const menuActions = useTripMenuActions();
  const dispatch = useAppDispatch();
  const assignedTrips = useAppSelector(
    (state) => state.dispatcher.dispatchResponse?.assigned_trips
  );
  const { payers } = usePayers();

  const onEventUpdated = useCallback(
    async (calendarUpdatedEvent: MbscEventUpdatedEvent) => {
      // This is where we want to do all our api updates and whatnot
      // We're starting with just reassigning. We'll use onEventCreated for new assignments
      // We also only support dragging on the dispatch screen so there's an assumption that
      // we're only dealing with DispatchTripRead instances here. If that changes we'll have
      // to firm up types
      if (
        calendarUpdatedEvent.oldEvent &&
        calendarUpdatedEvent.event.resource !==
          calendarUpdatedEvent.oldEvent.resource
      ) {
        const {
          hasPotentialMultiloadTripsAssigned,
          potentialMultiloadTripIds,
        } = getPotentialMultiloadCandidatesForUnassignedTrip(
          calendarUpdatedEvent.event.resource as string,
          calendarUpdatedEvent.event.trip,
          assignedTrips,
          payers?.results
        );

        if (
          hasPotentialMultiloadTripsAssigned &&
          potentialMultiloadTripIds.length > 0
        ) {
          const targetAssignmentId = calendarUpdatedEvent.event
            .resource as string;
          const existingMultiloadTripIds =
            getExistingMultiloadTripIdsForAssignment(
              potentialMultiloadTripIds,
              assignedTrips
            );

          if (existingMultiloadTripIds.length > 0) {
            menuActions.assignToExistingMultiLoad(
              calendarUpdatedEvent.event.trip.id,
              targetAssignmentId,
              existingMultiloadTripIds
            );
          } else {
            menuActions.multiloadAssign(
              calendarUpdatedEvent.event.trip.id,
              targetAssignmentId,
              potentialMultiloadTripIds
            );
          }
        } else {
          const originalTripData = calendarUpdatedEvent.event.trip;
          const newAssignmentId = calendarUpdatedEvent.event.resource as string;
          await menuActions.reassign(
            originalTripData.id,
            newAssignmentId,
            originalTripData.status,
            originalTripData.multiload_id
          );
        }
      }
      // We don't use onEventDragEnd because it immediately starts a dispatch poll that
      // resets the calendar event which looks weird
      dispatch(dispatcherSlice.actions.onCalendarEventDragEnd());
    },
    [dispatch, menuActions, assignedTrips, payers]
  );

  return onEventUpdated;
}
